import React from 'react';
import { TrendingUp, TrendingDown, Clock, Shield, Target, AlertCircle, Zap, Activity, Info, X, Share2, Copy } from 'lucide-react';

export interface SignalData {
    id: string;
    signal_type: 'BUY' | 'SELL';
    entry_type: 'MARKET' | 'LIMIT' | 'STOP';
    entry: number;
    entry_price?: number;
    stop_loss: number;
    take_profit: number;
    tp2?: number;
    probability: number;
    reason: string;
    result: 'active' | 'win' | 'loss' | 'pending' | 'expired';
    strategy_name: string;
    scanner_type: '313' | 'elite_53' | 'gemini-2.5-pro';
    tier?: string;
    risk_rating?: 'GREEN' | 'YELLOW' | 'RED';
    created_at: string;
    timeframe: string;
    rr?: string;
    confidence?: number;
    session?: string;
    setup?: string;
    timeframe_bias?: string;
    bias_4h?: string;
    reasoning?: string;
    price_at_signal?: number;
    model?: string;
    pnl_usd?: number;
    limit_entry?: number;
    structure_break?: string;
    liquidity_type?: string;
    ob_level?: number;
    fvg_range_low?: number;
    fvg_range_high?: number;
    key_level_swept?: string;
    invalidation?: string;
    status?: string;
    signal_number?: number;
    symbol: string;
    bias_15m?: string;
}

interface Props {
    signal: SignalData;
}

export const SignalCard: React.FC<Props> = ({ signal }) => {
    const [showModal, setShowModal] = React.useState(false);
    const isBuy = signal.signal_type === 'BUY';
    const isActive = signal.result === 'active';
    const isPending = signal.result === 'pending';
    const isWin = signal.result === 'win';
    const isLoss = signal.result === 'loss';

    return (
        <div
            onClick={() => setShowModal(true)}
            className={`group relative flex flex-col rounded-[2.5rem] border transition-all duration-700 cursor-pointer ${isActive
                    ? 'border-finance-accent/30 bg-[#0a0d14]/80 shadow-[0_20px_50px_-12px_rgba(212,165,55,0.2)]'
                    : isPending
                        ? 'border-blue-500/20 bg-[#0a0d14]/60 shadow-[0_10px_30px_-12px_rgba(59,130,246,0.1)] opacity-60 grayscale-[0.3]'
                        : isWin
                            ? 'border-emerald-500/30 bg-[#0a0d14]/60 shadow-[0_20px_50px_-12px_rgba(16,185,129,0.1)]'
                            : isLoss
                                ? 'border-rose-500/30 bg-[#0a0d14]/60 shadow-[0_20px_50px_-12px_rgba(244,63,94,0.1)]'
                                : 'border-white/5 bg-[#0a0d14]/40'
                } backdrop-blur-3xl overflow-hidden hover:scale-[1.02] hover:border-finance-accent/50 p-8`}>

            {/* Glossy Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent pointer-events-none"></div>

            {/* Top Bar: Status & Icon */}
            <div className="flex justify-between items-start mb-8 relative z-10">
                <div className="flex gap-4 items-center">
                    <div className={`p-4 rounded-2xl ${isBuy ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'} border border-current/10 shadow-lg`}>
                        {isBuy ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-2xl font-black text-white tracking-tight">{signal.symbol || 'XAUUSD'}</h3>
                            <span className="text-[11px] font-black px-2 py-0.5 rounded bg-white/5 text-gray-400 border border-white/10 uppercase tracking-widest leading-none">
                                {signal.timeframe}
                            </span>
                            {isPending && (
                                <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 uppercase tracking-widest leading-none animate-pulse">
                                    NOT ACTIVE
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full ${signal.status === 'tp1_hit' ? 'bg-amber-400'
                                    : isPending ? 'bg-blue-400'
                                        : 'bg-emerald-500'
                                } animate-pulse`}></div>
                            <span className={`text-[11px] font-black ${signal.status === 'tp1_hit' ? 'text-amber-400'
                                    : isPending ? 'text-blue-400'
                                        : 'text-emerald-400/80'
                                } uppercase tracking-[0.2em] leading-none`}>
                                {signal.status === 'tp1_hit' ? 'TP1 HIT - RUNNING'
                                    : isPending ? `${signal.entry_type || 'LIMIT'} • WAITING FOR ENTRY`
                                        : signal.result}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                    {signal.tier && (
                        <div className="bg-finance-accent/10 border border-finance-accent/20 px-3 py-1 rounded-full">
                            <span className="text-[9px] font-black text-finance-accent uppercase tracking-[0.3em]">
                                TIER {signal.tier}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center gap-1 text-gray-500 text-[11px] font-bold uppercase tracking-widest opacity-60">
                        <span className="text-finance-accent mr-1">REF: #{signal.signal_number || '0000'}</span>
                        <Clock size={10} />
                        {new Date(signal.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            </div>

            {/* TOP TIER: CORE ACTION (Refined) */}
            <div className="grid grid-cols-3 gap-4 mb-8 relative z-10">
                <div className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-all">
                    <span className="text-[8px] font-black text-gray-500 uppercase tracking-[0.2em]">{signal.entry_type || 'Entry'}</span>
                    <span className="text-lg font-black text-white font-mono">{signal.limit_entry || signal.entry_price || signal.entry || '—'}</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-rose-500/[0.02] border border-rose-500/10 hover:bg-rose-500/[0.05] transition-all">
                    <span className="text-[8px] font-black text-rose-500/60 uppercase tracking-[0.2em]">Risk (SL)</span>
                    <span className="text-lg font-black text-rose-500 font-mono">{signal.stop_loss || '—'}</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-emerald-500/[0.02] border border-emerald-500/10 hover:bg-emerald-500/[0.05] transition-all">
                    <span className="text-[8px] font-black text-emerald-500/60 uppercase tracking-[0.2em]">Target</span>
                    <span className="text-lg font-black text-emerald-500 font-mono">{signal.take_profit || '—'}</span>
                </div>
            </div>

            {/* CENTER TIER: AI ANALYSIS SUMMARY (Re-balanced) */}
            <div className="space-y-5 relative z-10 flex-grow">
                <div className="flex items-center justify-between bg-white/[0.01] border border-white/5 rounded-2xl p-4">
                    <div className="flex items-center gap-5">
                        <div className="flex flex-col">
                            <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-1">Confidence</span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-black text-white tracking-widest">{signal.probability || signal.confidence}%</span>
                                <div className="h-1 w-12 bg-white/5 rounded-full overflow-hidden">
                                    <div className="h-full bg-finance-accent" style={{ width: `${signal.probability || signal.confidence}%` }}></div>
                                </div>
                            </div>
                        </div>
                        <div className="h-6 w-px bg-white/10"></div>
                        <div className="flex flex-col">
                            <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-1">R:R Ratio</span>
                            <span className="text-sm font-black text-finance-accent tracking-widest">
                                {signal.rr || (signal.take_profit && (signal.limit_entry || signal.entry_price) && signal.stop_loss
                                    ? `1:${(Math.abs(signal.take_profit - (signal.limit_entry || signal.entry_price || 0)) / Math.abs((signal.limit_entry || signal.entry_price || 0) - signal.stop_loss)).toFixed(2)}`
                                    : 'N/A')}
                            </span>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-[7px] font-black text-gray-600 uppercase tracking-[0.3em] block mb-0.5">Strategy</span>
                        <span className="text-[9px] font-black text-white/40 uppercase tracking-tighter">
                            {signal.strategy_name?.replace(/_/g, ' ')}
                        </span>
                    </div>
                </div>

                {signal.reason && (
                    <div className="px-1">
                        <div className="flex items-center gap-1.5 mb-2.5 text-[8px] font-black text-gray-500 uppercase tracking-widest">
                            <Zap size={10} className="text-finance-accent" />
                            Rationale
                        </div>
                        <p className="text-[13px] text-gray-400 leading-relaxed font-medium line-clamp-2 italic">
                            "{signal.reason}"
                        </p>
                    </div>
                )}
            </div>

            {/* BUTTON TIER: INTERACTION (Symmetric) */}
            <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2">
                    <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-finance-accent/10 border border-finance-accent/20 text-finance-accent text-[8px] font-black uppercase tracking-widest hover:bg-finance-accent/20 transition-all">
                        <Info size={12} />
                        View Specs
                    </button>
                    <button className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-500 hover:text-white transition-all">
                        <Share2 size={12} />
                    </button>
                </div>
                <div className={`p-2 rounded-lg ${isBuy ? 'text-emerald-400 bg-emerald-400/5' : 'text-rose-400 bg-rose-400/5'} border border-current/10`}>
                    <AlertCircle size={14} />
                </div>
            </div>

            {/* INSTITUTIONAL POPUP MODAL (Perfect Alignment) */}
            {showModal && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 md:p-6 animate-in fade-in zoom-in-95 duration-300">
                    <div className="absolute inset-0 bg-[#03070b]/90 backdrop-blur-md" onClick={() => setShowModal(false)}></div>

                    <div className="relative w-full max-w-lg bg-[#0a0d14] border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] flex flex-col max-h-[85vh]">
                        {/* Header: Precision Centering */}
                        <div className={`px-8 py-6 border-b border-white/5 flex items-center justify-between ${isBuy ? 'bg-emerald-500/[0.02]' : 'bg-rose-500/[0.02]'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`p-3 rounded-xl ${isBuy ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'} border border-current/10`}>
                                    {isBuy ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-white tracking-widest uppercase">{signal.symbol || 'XAUUSD'} #{signal.signal_number || '0000'}</h3>
                                    <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.3em]">
                                        {signal.tier ? `Tier ${signal.tier}` : 'Professional Grade'} {signal.timeframe} Pipeline
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowModal(false);
                                }}
                                className="p-2 rounded-lg bg-white/5 border border-white/10 text-gray-500 hover:text-white transition-all relative z-[2010]"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Body: Structured Content */}
                        <div className="p-8 overflow-y-auto space-y-8 custom-scrollbar">

                            {/* Section 1: Execution Summary */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between px-1">
                                    <span className="text-[9px] font-black text-finance-accent uppercase tracking-[0.3em]">Execution Data</span>
                                    <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">Click to copy value</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { label: 'Type', value: signal.signal_type, color: isBuy ? 'text-emerald-400' : 'text-rose-400' },
                                        { label: 'Entry', value: signal.entry || signal.entry_price || '—' },
                                        { label: 'Stop Loss', value: signal.stop_loss || '—', color: 'text-rose-500' },
                                        { label: 'Take Profit', value: signal.take_profit || '—', color: 'text-emerald-500' },
                                    ].map((item, i) => (
                                        <div
                                            key={i}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (item.value) navigator.clipboard.writeText(item.value.toString());
                                            }}
                                            className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-finance-accent/30 cursor-pointer transition-all"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-[7px] font-black text-gray-600 uppercase tracking-widest mb-0.5">{item.label}</span>
                                                <span className={`text-sm font-black font-mono ${item.color || 'text-white'}`}>{item.value}</span>
                                            </div>
                                            <Copy size={10} className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Section 2: AI Logical Rationale */}
                            <div className="space-y-4">
                                <span className="text-[9px] font-black text-finance-accent uppercase tracking-[0.3em] px-1">AI Strategic Intelligence</span>
                                <div className="p-5 rounded-2xl bg-white/[0.01] border border-white/5 space-y-4">
                                    <p className="text-[13px] text-gray-400 leading-relaxed font-medium italic">
                                        "{signal.reason || "Analyzing multi-timeframe bias and institutional order flow to identify high-probability liquidation zones."}"
                                    </p>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                                            <span className="text-[7px] font-black text-gray-600 uppercase tracking-widest block mb-1">4H Bias</span>
                                            <span className={`text-[11px] font-black uppercase ${signal.bias_4h?.includes('BULL') ? 'text-emerald-400' : signal.bias_4h?.includes('BEAR') ? 'text-rose-400' : 'text-gray-400'}`}>
                                                {signal.bias_4h || signal.timeframe_bias || 'Neutral'}
                                            </span>
                                        </div>
                                        <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                                            <span className="text-[7px] font-black text-gray-600 uppercase tracking-widest block mb-1">15M Bias</span>
                                            <span className={`text-[11px] font-black uppercase ${signal.bias_15m?.includes('BULL') ? 'text-emerald-400' : signal.bias_15m?.includes('BEAR') ? 'text-rose-400' : 'text-gray-400'}`}>
                                                {signal.bias_15m || 'Neutral'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                                            <span className="text-[7px] font-black text-gray-600 uppercase tracking-widest block mb-1">Confidence</span>
                                            <span className="text-[11px] font-black text-white">{signal.probability || signal.confidence || 0}%</span>
                                        </div>
                                        <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                                            <span className="text-[7px] font-black text-gray-600 uppercase tracking-widest block mb-1">R:R</span>
                                            <span className="text-[11px] font-black text-finance-accent">{signal.rr || '1:2.0'}</span>
                                        </div>
                                    </div>

                                    {signal.invalidation && (
                                        <div className="p-4 rounded-xl bg-rose-500/5 border border-rose-500/10">
                                            <div className="flex items-center gap-2 mb-1">
                                                <AlertCircle size={10} className="text-rose-400" />
                                                <span className="text-[7px] font-black text-rose-400/80 uppercase tracking-widest">Invalidation Logic</span>
                                            </div>
                                            <p className="text-[11px] text-gray-400 font-medium leading-relaxed">
                                                {signal.invalidation}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Section 3: Pro Specs (Dynamic) */}
                            {(signal.session || signal.liquidity_type || signal.structure_break || signal.ob_level || signal.fvg_range_high) && (
                                <div className="space-y-3">
                                    <span className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.3em] px-1">Institutional Specs</span>
                                    <div className="grid grid-cols-1 gap-2">
                                        {signal.session && (
                                            <div className="flex items-center justify-between p-3 px-4 rounded-xl border border-white/5 bg-white/[0.01]">
                                                <div className="flex items-center gap-3">
                                                    <Clock size={12} className="text-indigo-400" />
                                                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Trading Session</span>
                                                </div>
                                                <span className="text-[11px] font-black text-white uppercase">{signal.session}</span>
                                            </div>
                                        )}
                                        {signal.liquidity_type && signal.liquidity_type !== 'NONE' && (
                                            <div className="flex items-center justify-between p-3 px-4 rounded-xl border border-white/5 bg-white/[0.01]">
                                                <div className="flex items-center gap-3">
                                                    <Activity size={12} className="text-amber-400" />
                                                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Liquidity Swept</span>
                                                </div>
                                                <span className="text-[11px] font-black text-white uppercase">{signal.liquidity_type}</span>
                                            </div>
                                        )}
                                        {signal.structure_break && signal.structure_break !== 'NONE' && (
                                            <div className="flex items-center justify-between p-3 px-4 rounded-xl border border-white/5 bg-white/[0.01]">
                                                <div className="flex items-center gap-3">
                                                    <TrendingUp size={12} className="text-emerald-400" />
                                                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Market Structure</span>
                                                </div>
                                                <span className="text-[11px] font-black text-white uppercase">{signal.structure_break}</span>
                                            </div>
                                        )}
                                        {signal.ob_level && (
                                            <div className="flex items-center justify-between p-3 px-4 rounded-xl border border-white/5 bg-white/[0.01]">
                                                <div className="flex items-center gap-3">
                                                    <Activity size={12} className="text-indigo-400" />
                                                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Order Block Level</span>
                                                </div>
                                                <span className="text-[11px] font-black text-white font-mono">{signal.ob_level}</span>
                                            </div>
                                        )}
                                        {signal.fvg_range_high && (
                                            <div className="flex items-center justify-between p-3 px-4 rounded-xl border border-white/5 bg-white/[0.01]">
                                                <div className="flex items-center gap-3">
                                                    <Zap size={12} className="text-finance-accent" />
                                                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">FVG Confluence</span>
                                                </div>
                                                <span className="text-[11px] font-black text-white font-mono">
                                                    {signal.fvg_range_low || '...'} - {signal.fvg_range_high}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Footer: Fine Details */}
                        <div className="px-8 py-5 border-t border-white/5 bg-white/[0.01] flex items-center justify-between">
                            <span className="text-[7px] font-black text-gray-600 uppercase tracking-[0.4em]">Proprietary Quant Pipeline</span>
                            <div className="flex items-center gap-3">
                                <span className="text-[8px] font-black text-gray-700 uppercase tracking-widest">Status:</span>
                                <span className="text-[11px] font-black text-emerald-500 uppercase tracking-tighter">Live Verified</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Animated Bottom Glow */}
            {isActive && (
                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-transparent via-finance-accent/40 to-transparent"></div>
            )}
        </div>
    );
};
